import { useState } from 'react';
import { Button, Dialog, InlineMessage, ToggleGroup } from '../../components';
import { useCreateExport, useExportDetail } from '../../hooks/use-exports';
import { MAX_SELECTED_RESULTS } from '../../hooks/use-result-selection';
import { downloadExport } from '../../services/download';
import { messageForCode } from '../../services/errors';
import type { ExportFormat, JobStatus, ResultFilters } from '../../types/api';
import { CONTENT_TYPE_LABEL, PLATFORM_LABEL, SORT_LABEL } from '../results/labels';
import { describeExportError, formatFileSize, formatInteger } from './messages';

const FORMAT_OPTIONS: Array<{ value: ExportFormat; label: string }> = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
];

export interface ExportDialogProps {
  searchId: string;
  filters: ResultFilters;
  selectedIds: string[];
  /** Coleta em andamento ou parcial vira aviso, não silêncio (RF-EU09). */
  searchStatus: JobStatus;
  onClose: () => void;
}

/**
 * Diálogo de exportação (SPEC-023 §9).
 *
 * Duas coisas que ele existe para garantir:
 *
 * 1. O usuário sabe o que vai ser exportado **antes** de confirmar. O backend
 *    congela os filtros no momento da criação; se a tela não declarar o
 *    recorte, o conteúdo do arquivo vira surpresa depois do download.
 * 2. Ninguém fica preso esperando. Depois do 202 o registro vive no banco:
 *    fechar o diálogo não cancela nada, e o export reaparece em /exportacoes.
 */
export function ExportDialog({
  searchId,
  filters,
  selectedIds,
  searchStatus,
  onClose,
}: ExportDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [exportId, setExportId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const create = useCreateExport();
  const detail = useExportDetail(exportId ?? undefined);

  const exceedsLimit = selectedIds.length > MAX_SELECTED_RESULTS;
  const collecting = searchStatus === 'PENDING' || searchStatus === 'RUNNING';

  const confirm = (): void => {
    if (exceedsLimit) return;

    create.mutate(
      {
        searchId,
        format,
        ...filters,
        ...(selectedIds.length > 0 ? { selectedIds } : {}),
      },
      { onSuccess: (result) => setExportId(result.exportId) },
    );
  };

  const download = (): void => {
    if (!exportId) return;
    setDownloadError(null);
    downloadExport(exportId).catch((error: unknown) =>
      setDownloadError(describeExportError(error)),
    );
  };

  const status = detail.data?.status;

  return (
    <Dialog
      title="Exportar resultados"
      description={
        exportId
          ? undefined
          : 'O arquivo é gerado em segundo plano e fica disponível para download.'
      }
      onClose={onClose}
      footer={
        exportId ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Fechar
            </Button>
            {status === 'COMPLETED' && <Button onClick={download}>Baixar arquivo</Button>}
            {status === 'FAILED' && (
              <Button
                onClick={() => {
                  setExportId(null);
                  create.reset();
                }}
              >
                Tentar de novo
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={confirm} loading={create.isPending} disabled={exceedsLimit}>
              Exportar
            </Button>
          </>
        )
      }
    >
      {exportId ? (
        <Tracking
          status={status}
          rowCount={detail.data?.rowCount ?? create.data?.rowCount ?? null}
          fileSizeBytes={detail.data?.fileSizeBytes ?? null}
          errorCode={detail.data?.errorCode ?? null}
          error={detail.isError ? describeExportError(detail.error) : null}
          downloadError={downloadError}
        />
      ) : (
        <>
          <ToggleGroup
            label="Formato"
            options={FORMAT_OPTIONS}
            value={[format]}
            onChange={([chosen]) => chosen && setFormat(chosen)}
            single
            hint="CSV abre em planilha; JSON preserva a estrutura completa."
          />

          <section className="rounded-md bg-surface-raised p-3">
            <h3 className="mb-2 text-sm font-medium text-text">O que será exportado</h3>
            <ul className="flex flex-col gap-1 text-sm text-text-muted">
              {describeFilters(filters).map((line) => (
                <li key={line}>{line}</li>
              ))}
              <li>
                {selectedIds.length > 0
                  ? `Apenas os ${formatInteger(selectedIds.length)} resultado(s) selecionados.`
                  : 'Todos os resultados do recorte filtrado — não apenas os que estão à vista.'}
              </li>
            </ul>
          </section>

          {collecting && (
            <InlineMessage tone="warning">
              A coleta ainda está em andamento. O arquivo será um retrato do que já foi coletado até
              agora.
            </InlineMessage>
          )}

          {searchStatus === 'PARTIAL' && (
            <InlineMessage tone="warning">
              Esta pesquisa é parcial: parte das plataformas não respondeu. O arquivo terá a mesma
              limitação.
            </InlineMessage>
          )}

          {exceedsLimit && (
            <InlineMessage tone="danger">
              A seleção passa de {formatInteger(MAX_SELECTED_RESULTS)} resultados. Reduza a seleção
              ou exporte o recorte inteiro sem selecionar nada.
            </InlineMessage>
          )}

          {/* Erro de escrita aparece junto do botão que o causou, nunca como
              página inteira de erro (SPEC-014 §13). */}
          {create.isError && (
            <InlineMessage tone="danger">{describeExportError(create.error)}</InlineMessage>
          )}
        </>
      )}
    </Dialog>
  );
}

function Tracking({
  status,
  rowCount,
  fileSizeBytes,
  errorCode,
  error,
  downloadError,
}: {
  status: string | undefined;
  rowCount: number | null;
  fileSizeBytes: number | null;
  errorCode: string | null;
  error: string | null;
  downloadError: string | null;
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* A contagem só existe depois do 202: antes disso o cliente conhece
          apenas os itens carregados, e inventar um número seria mentir. */}
      {rowCount !== null && (
        <p className="text-sm text-text">Recorte com {formatInteger(rowCount)} resultado(s).</p>
      )}

      {(status === undefined || status === 'PENDING') && (
        <InlineMessage tone="info">
          Exportação na fila. Você pode fechar esta janela — ela continua sendo gerada e fica no
          histórico de exportações.
        </InlineMessage>
      )}

      {status === 'RUNNING' && (
        <InlineMessage tone="info">
          Gerando o arquivo. Esta janela se atualiza sozinha.
        </InlineMessage>
      )}

      {status === 'COMPLETED' && (
        <InlineMessage tone="success">
          Arquivo pronto{fileSizeBytes !== null && ` — ${formatFileSize(fileSizeBytes)}`}.
        </InlineMessage>
      )}

      {status === 'FAILED' && (
        <InlineMessage tone="danger">
          {errorCode ? messageForCode(errorCode) : 'Não foi possível gerar a exportação.'}
        </InlineMessage>
      )}

      {status === 'EXPIRED' && (
        <InlineMessage tone="warning">
          Este arquivo expirou e foi removido. Gere a exportação novamente.
        </InlineMessage>
      )}

      {error && <InlineMessage tone="danger">{error}</InlineMessage>}
      {downloadError && <InlineMessage tone="danger">{downloadError}</InlineMessage>}
    </div>
  );
}

/** Declara o recorte em português (RF-EU02). */
export function describeFilters(filters: ResultFilters): string[] {
  const lines: string[] = [];

  lines.push(
    filters.platform
      ? `Plataforma: ${PLATFORM_LABEL[filters.platform]}`
      : 'Plataforma: todas as pesquisadas',
  );
  if (filters.type) lines.push(`Tipo: ${CONTENT_TYPE_LABEL[filters.type]}`);
  if (filters.author) lines.push(`Autor contendo "${filters.author}"`);
  if (filters.keyword) lines.push(`Texto contendo "${filters.keyword}"`);
  lines.push(`Ordenação: ${SORT_LABEL[filters.sort]}`);

  return lines;
}
